import { useCallback, useState } from "react";
import {
  CardDetailsTileUi,
  type CardDetailsTileUiProps,
} from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import type { UserCardSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";

export type CardDetailsTileLinkProps = Omit<
  CardDetailsTileUiProps,
  "isLoading" | "isSelected" | "onLongPress" | "onPress"
> & {
  /**
   * The morpheme-panels route prefix the tapped card opens (e.g.
   * `"/(nav)/(app)/decks/morpheme/[morpheme]"`). Supplied by the surface hosting the grid so the
   * pushed panel stays inside that tab's stack and its back control returns there — a card tapped in
   * a deck must not land the user in Browse.
   */
  href: string;
  /**
   * A grid supplies one shared navigator instead of mounting four mutation observers per tile.
   * The promise covers navigation preparation; pending UI remains local to the pressed tile.
   * Navigation failures are reported by the supplied navigator, as on the standalone path.
   */
  onNavigate?: (graphemeCard: MorphemeDetailsTileSlice) => Promise<unknown>;
  /** Exact actor user-card row represented by this deck tile. */
  selectionItem: UserCardSelectionItem;
};

export function CardDetailsTileLink(props: CardDetailsTileLinkProps) {
  const { href, onNavigate, ...buttonProps } = props;
  if (onNavigate !== undefined) {
    return <CardDetailsTileWithNavigation {...buttonProps} onNavigate={onNavigate} />;
  }
  return <SelfNavigatingCardDetailsTile {...buttonProps} href={href} />;
}

function SelfNavigatingCardDetailsTile({
  href,
  ...props
}: Omit<CardDetailsTileLinkProps, "onNavigate">) {
  const {
    push: { mutateAsync: pushCard },
  } = useMorphemePanelsHrefNavigator();
  const onNavigate = (pressedCard: MorphemeDetailsTileSlice) =>
    pushCard({
      morpheme: pressedCard.front,
      prefix: {
        href,
        // Open the morpheme page on its learning-progress info tab.
        params: { tab: "progress" },
      },
    });
  return <CardDetailsTileWithNavigation {...props} onNavigate={onNavigate} />;
}

function CardDetailsTileWithNavigation({
  graphemeCard,
  onGraphemePress,
  onNavigate,
  selectionItem,
  ...cardDetailsTileUiProps
}: Omit<CardDetailsTileLinkProps, "href" | "onNavigate"> & {
  onNavigate: (graphemeCard: MorphemeDetailsTileSlice) => Promise<unknown>;
}) {
  const { consumePress, isSelected, onLongPress } = useAppSelection([selectionItem]);
  const [pendingCount, setPendingCount] = useState(0);
  const handlePress = useCallback(
    (pressedCard: MorphemeDetailsTileSlice) => {
      if (consumePress()) {
        return;
      }
      onGraphemePress?.(pressedCard);
      setPendingCount((count) => count + 1);
      const settle = () => setPendingCount((count) => count - 1);
      // Both branches release this tile's loading state. The navigator owns error reporting.
      // Promise settlement is per invocation; useMutation's per-call onSettled can be replaced
      // by a subsequent tile's call on the shared observer and strand the earlier tile pending.
      void onNavigate(pressedCard).then(settle, settle);
    },
    [consumePress, onGraphemePress, onNavigate],
  );

  return (
    <CardDetailsTileUi
      graphemeCard={graphemeCard}
      isLoading={pendingCount > 0}
      isSelected={isSelected}
      onLongPress={onLongPress}
      onPress={handlePress}
      {...cardDetailsTileUiProps}
    />
  );
}
